import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import DeviceArchitectureModal from './DeviceArchitectureModal';
import SimProgressBar from './SimProgressBar';
import { themeColors } from '../theme';
import { SIM_COLORS } from '../utils/sim-colors';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';
import { runJob } from '../services/jobClient';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

// ─── Built-in device presets ─────────────────────────────────────────────────
// Fetched from the backend (GET /simulation/presets) on mount. Each preset is
// a pure parameter package assembled server-side from the material library —
// no material-specific bowing or alloy logic lives in the frontend.

// ─── Empty device used on first load ────────────────────────────────────────
// Exported so App.js can seed the lifted device state (Stage 2).
export const EMPTY_DEVICE = {
  substrate: { material: 'Glass', thickness_um: 1000 },
  frontContact:  { layers: [] },
  frontInterface: { delta_Ec: null, delta_Ev: null, Sn: 1e3, Sp: 1e3, Qit: 0, traps: [] },
  absorber:      { layers: [] },
  backInterface:  { delta_Ec: null, delta_Ev: null, Sn: 1e5, Sp: 1e5, Qit: 0, traps: [] },
  backContact:   { layers: [] },
  contacts: { front_WF: 4.1, front_Sn: 1e7, front_Sp: 1e7,
              back_WF: 5.2, back_Sn: 1e7, back_Sp: 1e7 },
  illumination: { intensity_suns: 1.0, spectrum: 'AM1.5G',
                  incident_side: 'front', temperature_K: 300,
                  front_reflection: 0, filters: [] },
};

// ─── Helper: get all layers from device (flat list for display) ──────────────
function allDeviceLayers(device) {
  return [
    ...(device.frontContact?.layers || []),
    ...(device.absorber?.layers || []),
    ...(device.backContact?.layers || []),
  ];
}

// ─── Junction type detection ────────────────────────────────────────────────
function detectJunctionType(device) {
  const layers = allDeviceLayers(device);
  if (layers.length < 2) return 'single layer';
  const types = layers.map(l => l.Nd > l.Na ? 'n' : 'p');
  const hasPN = types.some((t, i) => i > 0 && t !== types[i - 1]);
  const hasHetero = layers.some((l, i) => i > 0 && Math.abs(l.Eg - layers[i - 1].Eg) > 0.05);
  if (hasHetero && hasPN) return 'heterojunction';
  if (hasPN) return 'homojunction';
  if (hasHetero) return 'heterostructure';
  return `${types[0]}-type stack`;
}

// ─── Preset group labels (fallback if backend sends no `groups` array) ──────
const FALLBACK_GROUP_LABELS = {
  standard: 'Standard devices',
  perfect: 'Perfect devices (near-SQ)',
};

// ─── Custom presets (localStorage) ──────────────────────────────────────────
const CUSTOM_PRESETS_KEY = 'jvista_custom_presets_v2';
const MAX_CUSTOM_PRESETS = 5;

function loadCustomPresets() {
  try {
    const raw = localStorage.getItem(CUSTOM_PRESETS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveCustomPresets(presets) {
  localStorage.setItem(CUSTOM_PRESETS_KEY, JSON.stringify(presets));
}

// ─── Clone device for editor / preset load ──────────────────────────────────
const GRADABLE_PROPS = ['Eg', 'chi', 'eps_r', 'Nd', 'Na', 'tau_n_ns', 'tau_p_ns',
                        'mu_e', 'mu_h'];

// Deep-copy one trap, including its optional graded-density profile.
function cloneTrap(t) {
  const out = { ...t };
  if (Array.isArray(t.Nt_profile)) out.Nt_profile = t.Nt_profile.map(n => ({ ...n }));
  return out;
}

// Exported so App.js can deep-clone the device when seeding state.
export function cloneDevice(src) {
  const cloneLayers = (ls) => (ls || []).map(l => {
    const out = {
      ...l, id: l.id || Math.random().toString(36).slice(2),
      traps: l.traps ? l.traps.map(cloneTrap) : [],
    };
    // Deep-copy any per-property profile arrays
    GRADABLE_PROPS.forEach(p => {
      const key = `${p}_profile`;
      if (Array.isArray(l[key])) out[key] = l[key].map(n => ({ ...n }));
    });
    return out;
  });
  return {
    // Perfect (near-SQ) presets are flagged idealized — the backend then
    // skips published-record comparison warnings for them.
    idealized: !!src.idealized,
    substrate: { ...(src.substrate || { material: 'Glass', thickness_um: 1000 }) },
    frontContact: { layers: cloneLayers(src.frontContact?.layers) },
    frontInterface: { ...(src.frontInterface || { delta_Ec: null, delta_Ev: null, Sn: 1e3, Sp: 1e3, Qit: 0, traps: [] }) },
    absorber: { layers: cloneLayers(src.absorber?.layers) },
    backInterface: { ...(src.backInterface || { delta_Ec: null, delta_Ev: null, Sn: 1e5, Sp: 1e5, Qit: 0, traps: [] }) },
    backContact: { layers: cloneLayers(src.backContact?.layers) },
    contacts: { ...(src.contacts || { front_WF: 4.1, front_Sn: 1e7, front_Sp: 1e7, back_WF: 5.2, back_Sn: 1e7, back_Sp: 1e7 }) },
    illumination: cloneIllumination(src.illumination),
  };
}

// Deep-copy illumination (filters is an array of row objects). Presets
// loaded from the backend carry front_reflection here — honored as-is.
function cloneIllumination(src) {
  const base = src || { intensity_suns: 1.0, spectrum: 'AM1.5G',
                        incident_side: 'front', temperature_K: 300 };
  const out = { ...base };
  if (Array.isArray(base.filters)) out.filters = base.filters.map(f => ({ ...f }));
  return out;
}


// Free-text numeric input (same pattern as SciField below): keeps a local
// text state while focused so partial entries like "-", "-0." or "1e"
// survive keystrokes (the old type="number" + parseFloat-per-keystroke
// stomped them to NaN, making e.g. V_start = -0.4 untypeable). Only valid
// parses are committed (clamped to min/max when given); blur restores the
// formatted committed value.
function formatNum(v) {
  if (v === undefined || v === null || Number.isNaN(v)) return '';
  return String(v);
}

function NumField({ label, unit, value, onChange, min, max }) {
  const [local, setLocal] = useState('');
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setLocal(formatNum(value));
  }, [value]);

  return (
    <div className="field">
      <div className="field-label">
        <span dangerouslySetInnerHTML={{ __html: label }} />
        {unit && <span className="field-unit">{unit}</span>}
      </div>
      <input className="field-input" type="text" inputMode="decimal" value={local}
        onChange={e => {
          setLocal(e.target.value);
          let p = parseFloat(e.target.value);
          if (Number.isNaN(p)) return;   // partial entry — keep typing
          if (min != null && p < min) p = min;
          if (max != null && p > max) p = max;
          if (onChange) onChange(p);
        }}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; setLocal(formatNum(value)); }} />
    </div>
  );
}

// ─── SciField (scientific-notation text input) ──────────────────────────────
// Same free-text pattern as DeviceArchitectureModal.SciField, restyled with
// the simulator-panel field classes: accepts "1e12"-style entry, reformats
// to exponential notation on blur.
function formatSci(v) {
  if (v === undefined || v === null || isNaN(v)) return '';
  if (Math.abs(v) >= 1e4 || (Math.abs(v) < 1e-2 && v !== 0))
    return v.toExponential(2);
  return String(v);
}

function SciField({ label, unit, value, onChange }) {
  const [local, setLocal] = useState('');
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setLocal(formatSci(value));
  }, [value]);

  return (
    <div className="field">
      <div className="field-label">
        <span dangerouslySetInnerHTML={{ __html: label }} />
        {unit && <span className="field-unit">{unit}</span>}
      </div>
      <input className="field-input" type="text" value={local}
        onChange={e => {
          setLocal(e.target.value);
          const p = parseFloat(e.target.value);
          if (!isNaN(p) && onChange) onChange(p);
        }}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; setLocal(formatSci(value)); }} />
    </div>
  );
}

// ─── Contact interface-states sub-block (Phase 1B session 4) ────────────────
// Optional D_it/CNL descriptor at a contact-adjacent boundary. When the
// enable box is off, NOTHING is added to the payload (bit-identical old
// behavior). Energies are measured from Ev of the contact-adjacent layer.
function ContactIfStatesBlock({ label, value, onToggle, onChange }) {
  const st = value || {};
  return (
    <div style={{ marginTop: 8, paddingTop: 6,
      borderTop: '1px dashed var(--border, #30363d)' }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--mono)',
      }}>
        <input type="checkbox" checked={!!st.enabled}
          onChange={e => onToggle(e.target.checked)} />
        <span>{label}</span>
      </label>
      {st.enabled && (
        <div style={{ marginTop: 4 }}>
          <div className="field-row">
            <SciField label="D<sub>it</sub>" unit="cm⁻²eV⁻¹"
              value={st.Dit_cm2eV ?? 1e12}
              onChange={v => onChange({ Dit_cm2eV: v })} />
            <SciField label="CNL (from E<sub>v</sub>)" unit="eV"
              value={st.E_cnl_eV ?? 0.75}
              onChange={v => onChange({ E_cnl_eV: v })} />
          </div>
          <div className="field-row">
            <SciField label="&sigma;<sub>n</sub>" unit="cm²"
              value={st.sigma_n ?? 1e-15}
              onChange={v => onChange({ sigma_n: v })} />
            <SciField label="&sigma;<sub>p</sub>" unit="cm²"
              value={st.sigma_p ?? 1e-15}
              onChange={v => onChange({ sigma_p: v })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Optical filters (incident-spectrum) ────────────────────────────────────
// Row shapes mirror backend spectra.build_filter_transmission:
//   longpass/shortpass → cutoff_nm (+ optional in-band transmission)
//   bandpass           → center_nm + fwhm_nm (+ optional transmission)
//   nd                 → od (T = 10^-OD)
const FILTER_TYPES = [
  ['longpass',  'Longpass'],
  ['shortpass', 'Shortpass'],
  ['bandpass',  'Bandpass'],
  ['nd',        'ND'],
];

function defaultFilterRow(type) {
  if (type === 'bandpass') return { type, center_nm: 550, fwhm_nm: 40, transmission: 1.0 };
  if (type === 'nd')       return { type, od: 1.0 };
  return { type, cutoff_nm: 500, transmission: 1.0 };
}

// Serialize filter rows for the API payload — drops incomplete rows so the
// default (no filters) exactly preserves current behavior.
function serializeFilters(filters) {
  const out = [];
  (filters || []).forEach(f => {
    if (!f || !f.type) return;
    if (f.type === 'longpass' || f.type === 'shortpass') {
      if (!Number.isFinite(f.cutoff_nm)) return;
      const row = { type: f.type, cutoff_nm: f.cutoff_nm };
      if (Number.isFinite(f.transmission)) row.transmission = f.transmission;
      out.push(row);
    } else if (f.type === 'bandpass') {
      if (!Number.isFinite(f.center_nm) || !Number.isFinite(f.fwhm_nm)) return;
      const row = { type: 'bandpass', center_nm: f.center_nm, fwhm_nm: f.fwhm_nm };
      if (Number.isFinite(f.transmission)) row.transmission = f.transmission;
      out.push(row);
    } else if (f.type === 'nd') {
      if (Number.isFinite(f.od)) out.push({ type: 'nd', od: f.od });
      else if (Number.isFinite(f.transmission)) out.push({ type: 'nd', transmission: f.transmission });
    }
  });
  return out;
}

// Compact add/remove row editor for the optical-filter list.
function FiltersEditor({ filters, onChange }) {
  const rows = filters || [];
  const upd = (id, patch) =>
    onChange(rows.map(f => f.id === id ? { ...f, ...patch } : f));
  const retype = (id, type) =>
    onChange(rows.map(f => f.id === id ? { id: f.id, ...defaultFilterRow(type) } : f));
  const remove = (id) => onChange(rows.filter(f => f.id !== id));
  const add = () => onChange([...rows, {
    id: Math.random().toString(36).slice(2), ...defaultFilterRow('longpass'),
  }]);

  const miniLbl = { fontSize: 10, color: 'var(--text-2)', flex: '0 0 auto' };
  const numIn = (val, cb, title, width = 54) => (
    <input className="field-input" type="number" step="any" title={title}
      style={{ width, padding: '3px 5px', fontSize: 12, flex: '0 0 auto' }}
      value={val ?? ''}
      onChange={e => cb(e.target.value === '' ? null : parseFloat(e.target.value))} />
  );

  return (
    <div className="field" style={{ marginTop: 6 }}>
      <div className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Optical filters</span>
        <button type="button" onClick={add}
          style={{
            background: 'none', border: '1px dashed var(--border, #30363d)',
            color: 'var(--text-2)', borderRadius: 4, cursor: 'pointer',
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, padding: '1px 8px',
          }}>+ Add</button>
      </div>
      {rows.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>
          none — full spectrum
        </div>
      )}
      {rows.map(f => (
        <div key={f.id} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
          <select className="field-input" value={f.type}
            style={{ width: 96, padding: '3px 5px', fontSize: 12, flex: '0 0 auto' }}
            onChange={e => retype(f.id, e.target.value)}>
            {FILTER_TYPES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
          </select>
          {(f.type === 'longpass' || f.type === 'shortpass') && (
            <>
              <span style={miniLbl}>cutoff</span>
              {numIn(f.cutoff_nm, v => upd(f.id, { cutoff_nm: v }), 'Cutoff wavelength (nm)')}
              <span style={miniLbl}>nm · T</span>
              {numIn(f.transmission, v => upd(f.id, { transmission: v }), 'In-band transmission (0-1)', 44)}
            </>
          )}
          {f.type === 'bandpass' && (
            <>
              <span style={miniLbl}>ctr</span>
              {numIn(f.center_nm, v => upd(f.id, { center_nm: v }), 'Center wavelength (nm)')}
              <span style={miniLbl}>fwhm</span>
              {numIn(f.fwhm_nm, v => upd(f.id, { fwhm_nm: v }), 'Full width half max (nm)', 44)}
              <span style={miniLbl}>T</span>
              {numIn(f.transmission, v => upd(f.id, { transmission: v }), 'In-band transmission (0-1)', 44)}
            </>
          )}
          {f.type === 'nd' && (
            <>
              <span style={miniLbl}>OD</span>
              {numIn(f.od, v => upd(f.id, { od: v }), 'Optical density (T = 10^-OD)')}
            </>
          )}
          <button type="button" onClick={() => remove(f.id)} title="Remove filter"
            style={{
              background: 'none', border: 'none', color: 'var(--red, #ff4757)',
              cursor: 'pointer', fontSize: 13, padding: '0 2px', flex: '0 0 auto',
            }}>&times;</button>
        </div>
      ))}
    </div>
  );
}

// ─── Serialize layer for API payload ────────────────────────────────────────
// Exported (v1.1.0) so EQEPanel can detect a device edited since the last JV run.
export function serializeLayer(l) {
  const out = {
    material: l.material, name: l.name, thickness_um: l.thickness_um,
    Nd: l.Nd, Na: l.Na, tau_n_ns: l.tau_n_ns, tau_p_ns: l.tau_p_ns,
    Eg: l.Eg, chi: l.chi, eps_r: l.eps_r,
    m_e_eff: l.m_e_eff, m_h_eff: l.m_h_eff, mu_e: l.mu_e, mu_h: l.mu_h,
    B_rad: l.B_rad,
  };
  if (l.traps && l.traps.length > 0) {
    out.traps = l.traps.map(t => ({
      trap_type: t.type || 'acceptor',
      Et_eV: t.Et ?? 0.0,
      sigma_n: t.sigma_n ?? 1e-15,
      sigma_p: t.sigma_p ?? 1e-15,
      Nt: t.Nt ?? 1e14,
      energy_reference: t.Et_ref === 'Ec' ? 'from_Ec' : 'from_Ev',
      // Graded trap density: pass the {position_nm, value} nodes through
      // (>=2 nodes = graded; otherwise the scalar Nt above applies).
      ...(Array.isArray(t.Nt_profile) && t.Nt_profile.length >= 2 ? {
        Nt_profile: t.Nt_profile.map(n => ({
          position_nm: n.position_nm, value: n.value,
        })),
      } : {}),
    }));
  }
  // Per-property graded profiles. Each profile is list of {position_nm, value}.
  GRADABLE_PROPS.forEach(p => {
    const key = `${p}_profile`;
    const prof = l[key];
    if (Array.isArray(prof) && prof.length >= 2) {
      out[key] = prof.map(n => ({
        position_nm: n.position_nm, value: n.value,
      }));
    }
  });
  return out;
}

// ─── Main SimulatorPanel ───────────────────────────────────────────────────────
// Stage 2 lift: `device` and `simSettings` are owned by App.js and passed
// down as props (plus their setters). `setSim` is a local convenience
// helper that wraps setSimSettings with a key/value shape.
export default function SimulatorPanel({
  setResults, setLoading, setError, loading, theme = 'dark',
  results, savedSims = [], setSavedSims = () => {},
  selectedSims = [], setSelectedSims = () => {},
  setShowComparison = () => {},
  pendingDefects = null, clearPendingDefects = () => {},
  device, setDevice,
  simSettings, setSimSettings,
  openModalTrigger = 0,
}) {
  const c = themeColors(theme);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSolverPhysics, setShowSolverPhysics] = useState(false);
  const [activePreset, setActivePreset] = useState('');
  const [customPresets, setCustomPresets] = useState(loadCustomPresets);
  const [devicePresets, setDevicePresets] = useState([]);
  const [presetGroups, setPresetGroups] = useState([]);
  // True while GET /presets is in flight — gates the "No presets yet"
  // empty state so it can't flash before the fetch resolves.
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  // Real per-stage progress polled from the async job endpoint
  // ({current, total, stage} | null before the first poll returns).
  const [runProgress, setRunProgress] = useState(null);

  // Load built-in device presets from the backend once, on mount.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/simulation/presets`)
      .then(({ data }) => {
        if (cancelled) return;
        setDevicePresets(data.presets || []);
        setPresetGroups(data.groups || []);
      })
      .catch(() => {
        if (cancelled) return;
        setDevicePresets([]);
        setPresetGroups([]);
      })
      .finally(() => {
        if (!cancelled) setPresetsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── External trigger to open DeviceArchitectureModal ──────────────────
  // App.handleDeviceSpecLoad increments openModalTrigger after it
  // setDevice()s an AI-proposed spec; this effect catches the bump and
  // opens the modal so the user sees the pre-populated device. Skips
  // the initial 0 → no auto-open on mount.
  useEffect(() => {
    if (openModalTrigger > 0) setShowDeviceModal(true);
  }, [openModalTrigger]);

  const setSim = (key, val) => setSimSettings(s => ({ ...s, [key]: val }));
  const setIllum = (key, val) => setDevice(d => ({
    ...d, illumination: { ...d.illumination, [key]: val },
  }));

  // ── Solver physics (Phase 1B) helpers ─────────────────────────────
  // Default CNL = half the absorber Eg when known (mid-gap), else the
  // backend default 0.75 eV. Seeded into the sub-block state the first
  // time it is enabled so the SciField shows a concrete number.
  const absorberEg = device.absorber?.layers?.[0]?.Eg;
  const defaultCnl = Number.isFinite(absorberEg) ? absorberEg / 2 : 0.75;
  const toggleIfStates = (key, on) => setSimSettings(s => {
    const next = { ...(s[key] || {}), enabled: on };
    if (on && next.Dit_cm2eV == null) next.Dit_cm2eV = 1e12;
    if (on && next.E_cnl_eV == null) next.E_cnl_eV = defaultCnl;
    if (on && next.sigma_n == null) next.sigma_n = 1e-15;
    if (on && next.sigma_p == null) next.sigma_p = 1e-15;
    return { ...s, [key]: next };
  });
  const updIfStates = (key, patch) => setSimSettings(s => ({
    ...s, [key]: { ...(s[key] || {}), ...patch },
  }));

  // ── Pending defects from PL Analysis tab ──────────────────────────
  // Merge any incoming defects into the FIRST absorber layer's traps,
  // then fire a one-shot run via the runRequested flag (handleRun is
  // closed over the latest device, so we wait one render cycle before
  // calling it).
  const [runRequested, setRunRequested] = useState(false);

  useEffect(() => {
    if (!pendingDefects || pendingDefects.length === 0) return;
    setDevice(d => {
      const layers = d.absorber?.layers ? [...d.absorber.layers] : [];
      if (layers.length === 0) return d;
      const first = { ...layers[0] };
      first.traps = [...(first.traps || []), ...pendingDefects];
      layers[0] = first;
      return { ...d, absorber: { ...d.absorber, layers } };
    });
    setRunRequested(true);
    clearPendingDefects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDefects]);

  useEffect(() => {
    if (runRequested) {
      setRunRequested(false);
      handleRun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runRequested]);

  function loadPreset(preset) {
    setActivePreset(preset.id);
    // cloneDevice builds an explicit shape, so the "sweep" hint carried on
    // preset.device is stripped here — it never becomes a device property.
    setDevice(cloneDevice(preset.device));
    // Apply the preset's recommended JV sweep range (critical for high-Voc
    // devices, e.g. perovskites with Voc ≈ 1.3 V — the default V_end of
    // 1.1 V would never cross zero, showing Voc=0 / FF=0).
    const sweep = preset.device?.sweep || preset.sweep;
    if (sweep && typeof sweep === 'object') {
      setSimSettings(s => ({
        ...s,
        V_start: Number.isFinite(sweep.V_start) ? sweep.V_start : s.V_start,
        V_end:   Number.isFinite(sweep.V_end)   ? sweep.V_end   : s.V_end,
      }));
    }
  }

  function deleteCustomPreset(id) {
    const updated = customPresets.filter(p => p.id !== id);
    setCustomPresets(updated);
    saveCustomPresets(updated);
    if (activePreset === id) setActivePreset('');
  }

  // ── Device Architecture Modal handlers ───────────────────────
  function handleDeviceApply(newDevice) {
    setDevice(newDevice);
    setActivePreset('');
    setShowDeviceModal(false);
  }

  function handleDeviceSavePreset({ name, overwriteId, device: devData }) {
    const preset = {
      id: overwriteId || 'custom_' + Date.now(),
      name,
      desc: `${allDeviceLayers(devData).length} layers, custom`,
      isCustom: true,
      device: devData,
    };
    let updated;
    if (overwriteId) {
      updated = customPresets.map(p => p.id === overwriteId ? preset : p);
    } else {
      updated = [preset, ...customPresets].slice(0, MAX_CUSTOM_PRESETS);
    }
    setCustomPresets(updated);
    saveCustomPresets(updated);
  }

  // ── Preset dropdown grouping ──────────────────────────────────────
  // Group labels come from the backend `groups` array when present;
  // otherwise derive the group ids from the presets and use fallbacks.
  const presetGroupList = presetGroups.length > 0
    ? presetGroups
    : [...new Set(devicePresets.map(p => p.group || 'standard'))]
        .map(id => ({ id, label: FALLBACK_GROUP_LABELS[id] || id }));
  const activeBuiltinPreset = devicePresets.find(p => p.id === activePreset) || null;

  // Device summary
  const layers = allDeviceLayers(device);
  const totalUm = layers.reduce((s, l) => s + (l.thickness_um || 0), 0);
  // Short layer-stack summary (name + thickness only — no interface info).
  //   "FTO 200nm / MgZnO 100nm / CdTe 3.5μm"
  const fmtThick = (t_um) => {
    if (!t_um || t_um <= 0) return '';
    if (t_um >= 1) return `${t_um}μm`;
    return `${Math.round(t_um * 1000)}nm`;
  };
  const stackSummary = layers
    .map(l => `${(l.name || '').split(' ')[0]} ${fmtThick(l.thickness_um)}`.trim())
    .filter(Boolean)
    .join(' / ');

  // ── Stage 2: publish to WorkspaceContext ──────────────────────────
  // Minimal slice per design §3.3 (simulation block). Re-runs whenever the
  // device, the run status, or the saved-sims selection changes — same
  // signals the chat needs to track.
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    publish('simulation', {
      active_preset: activePreset,
      stack_summary: stackSummary,
      junction_type: detectJunctionType(device),
      total_thickness_um: totalUm,
      layer_count: layers.length,
      status: loading ? 'running' : 'idle',
      saved_sims_count: savedSims.length,
      selected_for_comparison: selectedSims.length,
    });
  }, [
    publish, activePreset, stackSummary, device, totalUm,
    layers.length, loading, savedSims.length, selectedSims.length,
  ]);

  async function handleRun() {
    setLoading(true);
    setError(null);
    setResults(null);
    setRunProgress(null);
    try {
      const payload = {
        idealized: !!device.idealized,
        // Domain-structured layers
        front_contact_layers: (device.frontContact?.layers || []).map(serializeLayer),
        front_interface: device.frontInterface || {},
        absorber_layers: (device.absorber?.layers || []).map(serializeLayer),
        back_interface: device.backInterface || {},
        back_contact_layers: (device.backContact?.layers || []).map(serializeLayer),

        // Fallback contacts (used when no contact layers defined)
        front_contact: {
          work_function: device.contacts?.front_WF ?? 4.1,
          Sn: device.contacts?.front_Sn ?? 1e7,
          Sp: device.contacts?.front_Sp ?? 1e7,
        },
        back_contact: {
          work_function: device.contacts?.back_WF ?? 5.2,
          Sn: device.contacts?.back_Sn ?? 1e7,
          Sp: device.contacts?.back_Sp ?? 1e7,
        },

        // Also send flat layers for backward compatibility
        layers: layers.map(serializeLayer),

        // Operating conditions
        temperature_K: simSettings.temperature_K,
        V_start: simSettings.V_start,
        V_end: simSettings.V_end,
        V_points: simSettings.V_points,
        illuminated: simSettings.illuminated,
        solver_preference: simSettings.solver_preference,
        intensity_suns: device.illumination?.intensity_suns ?? 1.0,
        spectrum: device.illumination?.spectrum || 'AM1.5G',

        // Illumination geometry & optics (2026-07). Defaults mirror the
        // backend schema so an untouched panel behaves exactly as before
        // (front, AM1.5G, no reflection, no filters).
        incident_side: device.illumination?.incident_side || 'front',
        front_reflection: device.illumination?.front_reflection ?? 0.0,
      };
      if ((device.illumination?.incident_side || 'front') === 'both') {
        payload.rear_intensity_suns = device.illumination?.rear_intensity_suns ?? 0.2;
      }
      if ((device.illumination?.spectrum || 'AM1.5G') === 'monochromatic') {
        payload.mono_wavelength_nm = device.illumination?.mono_wavelength_nm ?? 550;
        payload.mono_power_mW_cm2 = device.illumination?.mono_power_mW_cm2 ?? 100;
      }
      const activeFilters = serializeFilters(device.illumination?.filters);
      if (activeFilters.length > 0) payload.filters = activeFilters;

      // ── Solver physics (Phase 1B) — OPT-IN only. Each field is added
      // to the payload only when non-default, so an untouched panel
      // sends the exact pre-Phase-1B request (old behavior untouched).
      // 'auto' (V1.0): superposition + honesty guard, escalates to
      // coupled when a barrier signature is detected.
      if (simSettings.optics_model === 'tmm') {
        payload.optics_model = 'tmm';
      }
      // v1.1.0: always send the JV mode explicitly (default 'coupled' —
      // the only mode with voltage-dependent collection). Older saved
      // settings without jv_mode fall through to the backend default.
      if (simSettings.jv_mode) {
        payload.jv_mode = simSettings.jv_mode;
      }
      if (simSettings.contact_bc === 'robin') payload.contact_bc = 'robin';
      if (simSettings.chi_transport) payload.chi_transport = true;
      if (simSettings.wg_series_resistance) payload.wg_series_resistance = true;
      // Contact interface states (D_it/CNL descriptor; energies from Ev).
      // Consumed by the backend only under contact_bc='robin'.
      const buildIfStates = (st) => ({
        Dit_cm2eV: st.Dit_cm2eV ?? 1e12,
        E_cnl_eV:  st.E_cnl_eV ?? defaultCnl,
        sigma_n:   st.sigma_n ?? 1e-15,
        sigma_p:   st.sigma_p ?? 1e-15,
      });
      if (simSettings.front_interface_states?.enabled) {
        payload.front_contact.interface_states =
          buildIfStates(simSettings.front_interface_states);
      }
      if (simSettings.back_interface_states?.enabled) {
        payload.back_contact.interface_states =
          buildIfStates(simSettings.back_interface_states);
      }
      // Async job flow (V1.0): enqueue, then poll every ~1.8 s. The job
      // survives HTTP/proxy timeouts (coupled+robin solves can take
      // minutes) and reports real per-stage progress. The result body is
      // identical to the old synchronous POST /simulation/run response.
      const data = await runJob('run', payload, {
        onProgress: (p) => setRunProgress(p),
      });
      // Check for backend-level errors (HTTP 200 but success=false)
      if (data && data.success === false) {
        setError(data.error || 'Simulation failed (no details)');
        return;
      }
      // Keep the raw payload so downstream panels (EQE) can reuse the
      // same device definition without re-serializing.
      data._lastPayload = payload;
      // Attach device summary for AI context.  Keep this rich: the AI
      // tab uses it to reason about layer-level losses, interfaces, and
      // contact barriers.
      const layerCtx = (l) => {
        const o = {
          name: l.name, thickness_um: l.thickness_um, Eg: l.Eg, chi: l.chi,
          eps_r: l.eps_r, Nd: l.Nd, Na: l.Na,
          tau_n_ns: l.tau_n_ns, tau_p_ns: l.tau_p_ns,
          mu_e: l.mu_e, mu_h: l.mu_h,
        };
        GRADABLE_PROPS.forEach(p => {
          const key = `${p}_profile`;
          if (Array.isArray(l[key]) && l[key].length >= 2) o[key] = l[key];
        });
        return o;
      };
      data._deviceContext = {
        frontContact: (device.frontContact?.layers || []).map(layerCtx),
        absorber: (device.absorber?.layers || []).map(l => ({
          ...layerCtx(l),
          traps: l.traps || [],
        })),
        backContact: (device.backContact?.layers || []).map(layerCtx),
        frontInterface: device.frontInterface || {},
        backInterface: device.backInterface || {},
        contacts: device.contacts || {},
        illumination: device.illumination || {},
      };
      setResults(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; ');
      } else if (detail && typeof detail === 'object') {
        msg = detail.msg || detail.message || JSON.stringify(detail);
      } else {
        msg = err.message || 'Simulation failed';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <aside className="simulator-panel">

      {/* ── Device Presets ─────────────────────────────────────────── */}
      <div className="panel-section">
        <div className="section-label">Device Presets</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {presetsLoading && devicePresets.length === 0 && (
            <div style={{
              padding: '10px 12px', textAlign: 'center',
              color: c.textMuted, fontSize: 12, fontStyle: 'italic',
            }}>
              Loading presets…
            </div>
          )}
          {/* Empty state only AFTER the fetch resolved with zero presets —
              never flashed while GET /presets is still in flight. */}
          {!presetsLoading && devicePresets.length === 0 && customPresets.length === 0 && (
            <div style={{
              padding: '10px 12px', textAlign: 'center',
              color: c.textMuted, fontSize: 12,
              border: `1px dashed ${c.border}`, borderRadius: 6,
            }}>
              No presets yet — click <strong>Define Device</strong> below to
              build a layer stack, then save as a custom preset.
            </div>
          )}
          {presetGroupList.map(g => {
            const groupPresets = devicePresets.filter(
              p => (p.group || 'standard') === g.id
            );
            if (groupPresets.length === 0) return null;
            const selectedInGroup = groupPresets.some(p => p.id === activePreset)
              ? activePreset : '';
            return (
              <div className="field" key={g.id}>
                <div className="field-label"><span>{g.label}</span></div>
                <select
                  className="field-input"
                  value={selectedInGroup}
                  onChange={e => {
                    const preset = groupPresets.find(p => p.id === e.target.value);
                    // Loading a preset sets activePreset, which resets the
                    // other group's dropdown back to its placeholder.
                    if (preset && !preset.disabled) loadPreset(preset);
                  }}
                >
                  <option value="">— select —</option>
                  {groupPresets.map(p => (
                    <option key={p.id} value={p.id} disabled={!!p.disabled}
                      title={p.disabled ? p.disabledReason : (p.desc || p.description || '')}>
                      {p.name}{p.disabled ? ' (coming soon)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
          {activeBuiltinPreset && (activeBuiltinPreset.description || activeBuiltinPreset.desc) && (
            <div style={{
              fontSize: 11, color: c.textMuted, lineHeight: 1.45,
              padding: '0 2px',
            }}>
              {activeBuiltinPreset.description || activeBuiltinPreset.desc}
            </div>
          )}
          {customPresets.map(p => {
            const isSelected = activePreset === p.id;
            return (
              <button
                key={p.id}
                onClick={() => loadPreset(p)}
                title={p.desc || ''}
                style={{
                  position: 'relative',
                  width: '100%', padding: '8px 12px', textAlign: 'left',
                  background: isSelected ? c.accent : c.bgSecondary,
                  color:      isSelected ? c.btnPrimaryText : c.text,
                  border: `1px solid ${isSelected ? c.accent : c.border}`,
                  borderRadius: 6, cursor: 'pointer',
                  fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
                }}
              >
                {p.name}
                <span style={{
                  marginLeft: 8, fontSize: 10, padding: '1px 5px',
                  borderRadius: 2, background: 'rgba(148,163,184,0.15)',
                  color: c.textMuted, letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}>custom</span>
                <span
                  onClick={e => { e.stopPropagation(); deleteCustomPreset(p.id); }}
                  style={{
                    position: 'absolute', top: 4, right: 6,
                    fontSize: 14, color: c.red, cursor: 'pointer',
                    lineHeight: 1, padding: '0 2px',
                  }}
                  title="Delete preset"
                >&times;</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Device Stack Preview ──────────────────────────────────── */}
      <div className="panel-section">
        <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Device Stack</span>
          <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>
            {layers.length} layers · {totalUm.toFixed(2)} μm
          </span>
        </div>

        {/* Domain-colored stack bar */}
        <div className="stack-bar">
          {(device.frontContact?.layers || []).map((l, i) => {
            const pct = totalUm > 0 ? (l.thickness_um / totalUm) * 100 : 0;
            return (
              <div key={`fc-${i}`} title={`${l.name} — ${l.thickness_um} μm`}
                style={{ width: `${pct}%`, background: '#3b82f6', height: '100%',
                  borderRight: '1px solid var(--bg-0)', transition: 'width 0.3s' }} />
            );
          })}
          {(device.absorber?.layers || []).map((l, i) => {
            const pct = totalUm > 0 ? (l.thickness_um / totalUm) * 100 : 0;
            return (
              <div key={`abs-${i}`} title={`${l.name} — ${l.thickness_um} μm`}
                style={{ width: `${pct}%`, background: '#ef4444', height: '100%',
                  borderRight: '1px solid var(--bg-0)', transition: 'width 0.3s' }} />
            );
          })}
          {(device.backContact?.layers || []).map((l, i) => {
            const pct = totalUm > 0 ? (l.thickness_um / totalUm) * 100 : 0;
            return (
              <div key={`bc-${i}`} title={`${l.name} — ${l.thickness_um} μm`}
                style={{ width: `${pct}%`, background: '#a855f7', height: '100%',
                  borderRight: '1px solid var(--bg-0)', transition: 'width 0.3s' }} />
            );
          })}
        </div>

        {/* Summary text — layer names + thickness, no interface info */}
        <div style={{ marginBottom: 6, fontSize: 13, fontFamily: 'var(--mono)',
                       color: 'var(--text-1)', lineHeight: 1.5 }}>
          {stackSummary}
        </div>

        {/* Device summary */}
        <div className="device-summary">
          {detectJunctionType(device)} · {layers.length > 1 ? `${layers.length - 1} interface${layers.length - 1 !== 1 ? 's' : ''}` : 'no interfaces'}
          {layers.some(l => l.graded) && ' · graded'}
        </div>

        {/* Define Device button */}
        <button className="define-device-btn" onClick={() => setShowDeviceModal(true)}>
          Define Device &#9656;
        </button>
      </div>

      {/* Device Architecture Modal */}
      <DeviceArchitectureModal
        isOpen={showDeviceModal}
        onClose={() => setShowDeviceModal(false)}
        onApply={handleDeviceApply}
        device={device}
        onSavePreset={handleDeviceSavePreset}
        customPresets={customPresets}
      />

      {/* ── Illumination ───────────────────────────────────────────── */}
      <div className="panel-section">
        <div className="section-label">Illumination</div>
        <div className="toggle-row">
          <span className="toggle-label">Illuminated</span>
          <label className="toggle">
            <input type="checkbox" checked={simSettings.illuminated}
              onChange={e => setSim('illuminated', e.target.checked)} />
            <span className="toggle-slider" />
          </label>
        </div>
        <div className="field-row">
          <NumField label="Intensity" unit="suns"
            value={device.illumination?.intensity_suns ?? 1.0}
            onChange={v => setIllum('intensity_suns', v)}
            step={0.1} min={0.01} max={1000} />
          <NumField label="T" unit="K"
            value={simSettings.temperature_K}
            onChange={v => setSim('temperature_K', v)}
            step={5} min={150} max={500} />
        </div>
        <div className="field-row">
          <div className="field">
            <div className="field-label"><span>Incident side</span></div>
            <select className="field-input"
              value={device.illumination?.incident_side || 'front'}
              onChange={e => setIllum('incident_side', e.target.value)}>
              <option value="front">Front</option>
              <option value="rear">Rear</option>
              <option value="both">Both (bifacial)</option>
            </select>
          </div>
          <div className="field">
            <div className="field-label"><span>Spectrum</span></div>
            <select className="field-input"
              value={device.illumination?.spectrum || 'AM1.5G'}
              onChange={e => setIllum('spectrum', e.target.value)}>
              <option value="AM1.5G">AM1.5G</option>
              <option value="AM0">AM0</option>
              <option value="monochromatic">Monochromatic</option>
            </select>
          </div>
        </div>
        {(device.illumination?.incident_side || 'front') === 'both' && (
          <NumField label="Rear intensity" unit="suns"
            value={device.illumination?.rear_intensity_suns ?? 0.2}
            onChange={v => setIllum('rear_intensity_suns', v)}
            step={0.05} min={0} max={100} />
        )}
        {(device.illumination?.spectrum || 'AM1.5G') === 'monochromatic' && (
          <div className="field-row">
            <NumField label="&lambda;" unit="nm"
              value={device.illumination?.mono_wavelength_nm ?? 550}
              onChange={v => setIllum('mono_wavelength_nm', v)}
              step={10} min={250} max={3900} />
            <NumField label="Power" unit="mW/cm²"
              value={device.illumination?.mono_power_mW_cm2 ?? 100}
              onChange={v => setIllum('mono_power_mW_cm2', v)}
              step={10} min={0.1} max={10000} />
          </div>
        )}
        <NumField label="Front reflection" unit="%"
          value={Math.round((device.illumination?.front_reflection ?? 0) * 1000) / 10}
          onChange={v => setIllum('front_reflection',
            isNaN(v) ? 0 : Math.min(Math.max(v, 0), 100) / 100)}
          step={1} min={0} max={100} />
        <FiltersEditor
          filters={device.illumination?.filters || []}
          onChange={fs => setIllum('filters', fs)}
        />
      </div>

      {/* ── Voltage Sweep ──────────────────────────────────────────── */}
      <div className="panel-section">
        <div className="section-label">Voltage Sweep</div>
        <div className="field-row">
          <NumField label="V<sub>start</sub>" unit="V"
            value={simSettings.V_start}
            onChange={v => setSim('V_start', v)} step={0.05} />
          <NumField label="V<sub>end</sub>" unit="V"
            value={simSettings.V_end}
            onChange={v => setSim('V_end', isNaN(v) ? 1.1 : v)} step={0.05} />
        </div>
        <div className="field">
          <div className="field-label"><span>Points</span></div>
          <select className="field-input" value={simSettings.V_points}
            onChange={e => setSim('V_points', parseInt(e.target.value))}>
            <option value={30}>30 — Fast</option>
            <option value={60}>60 — Standard</option>
            <option value={100}>100 — High res</option>
          </select>
        </div>
      </div>

      {/* ── Solver physics (Phase 1B) ──────────────────────────────── */}
      <div className="panel-section">
        <div
          className="section-label"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setShowSolverPhysics(s => !s)}
          title="Opt-in solver physics — all defaults preserve the historical solver behavior exactly."
        >
          {showSolverPhysics ? '▾' : '▸'} Solver physics
        </div>
        {showSolverPhysics && (
          <>
            <div className="field" style={{ marginTop: 6 }}>
              <div className="field-label"><span>JV mode</span></div>
              <select className="field-input"
                value={simSettings.jv_mode || 'coupled'}
                onChange={e => setSim('jv_mode', e.target.value)}
                title="Coupled solves the full illuminated device at every bias — the only mode with voltage-dependent collection (J_ph(V)), light/dark crossover and back-barrier kink/rollover. Superposition builds J = Jsc + J_dark(V) with a constant photocurrent. Auto runs superposition first and escalates to coupled only when a barrier signature is detected">
                <option value="coupled">Coupled illuminated (default — voltage-dependent collection)</option>
                <option value="superposition">Superposition (fast; constant photocurrent, no V-dependent collection)</option>
                <option value="auto">Auto (superposition-first; coupled only if a barrier is detected)</option>
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.4, marginTop: 2 }}>
                Coupled solves the full illuminated device at every bias, so
                the photocurrent J<sub>ph</sub>(V) = J<sub>light</sub> −
                J<sub>dark</sub> carries the bias-dependent collection
                (shown on the JV chart with η<sub>c</sub> at SC/MPP/V<sub>oc</sub>);
                required for back-barrier kink/rollover/crossover studies.
                Superposition is faster but its photocurrent is constant by
                construction. Reverse bias: set V start below 0.
              </div>
            </div>
            <div className="field">
              <div className="field-label"><span>Optics model</span></div>
              <select className="field-input"
                value={simSettings.optics_model || 'beer_lambert'}
                onChange={e => setSim('optics_model', e.target.value)}
                title="TMM computes coherent thin-film interference from measured n,k dispersion data; reflection emerges from the stack">
                <option value="beer_lambert">Beer-Lambert (default)</option>
                <option value="tmm">Transfer-matrix (TMM, n/k dispersion)</option>
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.4, marginTop: 2 }}>
                TMM computes thin-film interference and reflection from
                measured n,k data (sourced per material; analytic fallback
                otherwise). Planar stacks without an ARC will honestly
                reflect more than the idealized Beer-Lambert default.
              </div>
            </div>
            <div className="field">
              <div className="field-label"><span>Contact model</span></div>
              <select className="field-input"
                value={simSettings.contact_bc || 'pinned'}
                onChange={e => setSim('contact_bc', e.target.value)}
                title="Robin makes contact work function and Sn/Sp physically active">
                <option value="pinned">Pinned (default)</option>
                <option value="robin">Robin (finite-S, work-function active)</option>
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.4, marginTop: 2 }}>
                Robin makes contact work function and S<sub>n</sub>/S<sub>p</sub> physically
                active. Contact WF/S are set in Define Device &rsaquo; Contacts.
              </div>
            </div>
            <div className="toggle-row">
              <span className="toggle-label"
                title="Adds the band-parameter dipole to Poisson so band-offset asymmetry (spike vs cliff, graded chi) drives transport">
                χ in transport (band-edge gradients drive current)
              </span>
              <label className="toggle">
                <input type="checkbox" checked={!!simSettings.chi_transport}
                  onChange={e => setSim('chi_transport', e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="toggle-row">
              <span className="toggle-label"
                title="Harmonic-mean (series) edge diffusivity on the wide-gap anchor so a low-mobility transport layer actually limits extraction">
                WG series resistance
              </span>
              <label className="toggle">
                <input type="checkbox" checked={!!simSettings.wg_series_resistance}
                  onChange={e => setSim('wg_series_resistance', e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <ContactIfStatesBlock
              label="Front contact interface states"
              value={simSettings.front_interface_states}
              onToggle={on => toggleIfStates('front_interface_states', on)}
              onChange={patch => updIfStates('front_interface_states', patch)}
            />
            <ContactIfStatesBlock
              label="Back contact interface states"
              value={simSettings.back_interface_states}
              onToggle={on => toggleIfStates('back_interface_states', on)}
              onChange={patch => updIfStates('back_interface_states', patch)}
            />
            {(simSettings.front_interface_states?.enabled ||
              simSettings.back_interface_states?.enabled) &&
              simSettings.contact_bc !== 'robin' && (
              <div style={{
                fontSize: 11, color: c.amber || '#fb8500',
                lineHeight: 1.4, marginTop: 6, fontFamily: 'var(--mono)',
              }}>
                Interface states are consumed only under the Robin contact model
                — set Contact model to Robin for them to take effect.
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Advanced ──────────────────────────────────────────────── */}
      <div className="panel-section">
        <div
          className="section-label"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setShowAdvanced(s => !s)}
          title="Expert settings — leave at defaults unless you know why you're changing them."
        >
          {showAdvanced ? '▾' : '▸'} Advanced
        </div>
        {showAdvanced && (
          <div className="field" style={{ marginTop: 6 }}>
            <div className="field-label">
              <span>Solver</span>
              <span className="field-unit">expert</span>
            </div>
            <select className="field-input" value={simSettings.solver_preference}
              onChange={e => setSim('solver_preference', e.target.value)}
              title="auto/hetero → HeteroSolver (default). sesame → Sesame, explicit error if device unsupported (no silent fallback).">
              <option value="auto">auto (HeteroSolver)</option>
              <option value="hetero">hetero (explicit)</option>
              <option value="sesame">sesame (opt-in, no fallback)</option>
            </select>
          </div>
        )}
      </div>

      {/* ── Run ────────────────────────────────────────────────────── */}
      <div className="run-section">
        <button className={`run-btn ${loading ? 'loading' : ''}`}
          onClick={handleRun} disabled={loading}>
          <span>{loading ? '⟳  Solving...' : '▶  Run Simulation'}</span>
        </button>

        {/* Per-stage solve progress polled from the async job endpoint.
            Falls back to a time estimate until the first poll returns. */}
        <SimProgressBar
          active={loading}
          totalPoints={6}
          perPointSec={3}
          label="Solving"
          c={c}
          progress={runProgress}
        />

        {/* Save to batch */}
        {results && (
          <div style={{ marginTop: 8 }}>
            {savedSims.length < 10 ? (
              <button
                onClick={() => {
                  const n = savedSims.length;
                  const defaultName = `Sim ${n + 1}: ${activePreset || 'Custom'}`;
                  const name = window.prompt('Name this simulation:', defaultName) || defaultName;
                  setSavedSims([...savedSims, {
                    id: Date.now(),
                    name,
                    color: SIM_COLORS[n % SIM_COLORS.length],
                    results: { ...results },
                    device: cloneDevice(device),
                    timestamp: new Date().toLocaleTimeString(),
                    metrics: {
                      Voc: results.metrics?.Voc,
                      Jsc: results.metrics?.Jsc,
                      FF:  results.metrics?.FF,
                      PCE: results.metrics?.PCE,
                    },
                  }]);
                }}
                style={{
                  width: '100%', padding: '6px 12px',
                  background: theme === 'dark' ? '#1a472a' : '#dcfce7',
                  color: theme === 'dark' ? '#00ff88' : '#15803d',
                  border: `1px solid ${theme === 'dark' ? '#00ff88' : '#15803d'}`,
                  borderRadius: 6, cursor: 'pointer',
                  fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
                }}
              >
                💾 Save to Batch ({savedSims.length}/10)
              </button>
            ) : (
              <div style={{
                color: c.red, fontSize: 12,
                textAlign: 'center', marginTop: 4,
                fontFamily: 'IBM Plex Mono, monospace',
              }}>
                Batch full (10/10) — clear to save more
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Saved Simulations Batch ────────────────────────────────── */}
      {savedSims.length > 0 && (
        <div className="panel-section">
          <div className="section-label" style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>Batch ({savedSims.length}/10)</span>
            <button
              onClick={() => { setSavedSims([]); setSelectedSims([]); }}
              title="Clear all saved sims"
              style={{
                background: 'none', border: 'none', color: c.red,
                cursor: 'pointer', fontSize: 11, fontFamily: 'IBM Plex Mono, monospace',
              }}
            >Clear</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {savedSims.map((sim) => {
              const checked = selectedSims.includes(sim.id);
              return (
                <div key={sim.id} style={{
                  border: `1px solid ${c.border}`, borderRadius: 6,
                  background: c.bgSecondary, padding: '6px 8px',
                  fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    justifyContent: 'space-between',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: sim.color, flexShrink: 0,
                      }} />
                      <span style={{ color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sim.name}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setSavedSims(savedSims.filter(s => s.id !== sim.id));
                        setSelectedSims(selectedSims.filter(id => id !== sim.id));
                      }}
                      title="Delete"
                      style={{
                        background: 'none', border: 'none', color: c.red,
                        cursor: 'pointer', fontSize: 13, padding: 0,
                      }}
                    >🗑</button>
                  </div>
                  <div style={{ color: c.textMuted, marginTop: 2, fontSize: 11 }}>
                    {sim.metrics?.Voc != null && `Voc=${Number(sim.metrics.Voc).toFixed(3)} `}
                    {sim.metrics?.Jsc != null && `Jsc=${Math.abs(Number(sim.metrics.Jsc)).toFixed(1)} `}
                    {sim.metrics?.PCE != null && `PCE=${Number(sim.metrics.PCE).toFixed(1)}%`}
                  </div>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    marginTop: 4, color: c.textMuted, cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        if (checked) setSelectedSims(selectedSims.filter(id => id !== sim.id));
                        else setSelectedSims([...selectedSims, sim.id]);
                      }}
                    />
                    <span>compare</span>
                  </label>
                </div>
              );
            })}
          </div>
          {selectedSims.length >= 2 && (
            <button
              onClick={() => setShowComparison(true)}
              style={{
                width: '100%', marginTop: 8, padding: '6px 12px',
                background: c.btnPrimary, color: c.btnPrimaryText,
                border: 'none', borderRadius: 6, cursor: 'pointer',
                fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
              }}
            >
              Compare Selected ({selectedSims.length}) →
            </button>
          )}
        </div>
      )}

    </aside>
  );
}
